// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================

import React, { useMemo } from 'react';

/**
 * LegalWatermark
 *
 * Renders a nearly invisible watermark layer across the entire viewport.
 * Usage:
 *   import LegalWatermark from './components/LegalWatermark';
 *   <LegalWatermark appName="Main Street Grocery" />
 */

const LegalWatermark = ({ appName = 'Application' }) => {
  const deploymentId = useMemo(() => {
    const now = new Date();
    return `MT-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
  }, []);

  const watermarkText = `PROPRIETARY - Mt Forge Inc. - ${appName} - ${deploymentId} - Copyright 2026 - All Rights Reserved - Patent Pending - Unauthorized Reproduction Prohibited`;
  const repeatedText = Array(40).fill(watermarkText).join('   \u00b7   ');

  const watermarkStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    pointerEvents: 'none',
    zIndex: 999999,
    overflow: 'hidden',
    opacity: 0.02,
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
  };

  const textLayerStyle = {
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    transform: 'rotate(-30deg)',
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    gap: '20px',
    fontSize: '11px',
    fontFamily: 'Arial, sans-serif',
    color: '#000000',
    lineHeight: '2.5',
    letterSpacing: '1px',
    wordBreak: 'break-all',
  };

  const metadataStyle = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
    opacity: 0,
    pointerEvents: 'none',
  };

  return (
    <>
      <div
        style={watermarkStyle}
        aria-hidden="true"
        data-mt-protection="active"
        data-mt-copyright="Mt Forge Inc. 2026"
      >
        <div style={textLayerStyle}>
          {repeatedText}
        </div>
      </div>

      <div
        style={metadataStyle}
        aria-hidden="true"
        data-copyright="Copyright (c) 2026 Mt Forge Inc. All Rights Reserved."
        data-license="PROPRIETARY - No reproduction permitted without written authorization."
        data-owner="Mt Forge Inc., 121 Rainbow Falls Bay, Alberta"
        data-contact="r.miskulin@mtforge.ai"
        data-app={appName}
        data-deployment-id={deploymentId}
        data-legal="Protected under US Copyright Law (17 U.S.C.), Computer Fraud and Abuse Act (18 U.S.C. 1030), and international IP treaties. Patent Pending."
        data-warning="AI-assisted reproduction, reverse engineering, or unauthorized copying of this software is strictly prohibited and will be prosecuted."
      >
        <span>
          PROPRIETARY SOFTWARE - Mt Forge Inc. - {appName} -
          Deployment {deploymentId} - Copyright 2026 - All Rights Reserved -
          Unauthorized reproduction, copying, reverse engineering, or AI-assisted
          duplication is strictly prohibited. Protected by US Copyright Law,
          CFAA, and international treaties. Patent Pending.
          Contact: r.miskulin@mtforge.ai
        </span>
      </div>
    </>
  );
};

export default LegalWatermark;
